import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker - use unpkg CDN (more reliable than cdnjs)
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface ProtectedPDFViewerProps {
  url: string;
  protectionStyles?: React.CSSProperties;
}

export const ProtectedPDFViewer: React.FC<ProtectedPDFViewerProps> = ({ url, protectionStyles }) => {
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRendering, setIsRendering] = useState(false);

  // Load PDF document
  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Clean up previous PDF
        if (pdf) {
          pdf.destroy();
          setPdf(null);
        }
        
        // Reset state
        setCurrentPage(1);
        setNumPages(0);
        
        // Clear previous canvas
        if (canvasContainerRef.current) {
          canvasContainerRef.current.innerHTML = '';
        }
        
        const loadingTask = pdfjsLib.getDocument(url);
        const pdfDoc = await loadingTask.promise;
        setPdf(pdfDoc);
        setNumPages(pdfDoc.numPages);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError('Failed to load PDF document. Please try refreshing the page.');
        setLoading(false);
      }
    };

    if (url) {
      loadPDF();
    }

    // Cleanup function
    return () => {
      if (pdf) {
        pdf.destroy();
      }
    };
  }, [url]); // Remove pdf from dependencies to avoid infinite loop

  // Render current page
  useEffect(() => {
    if (!pdf || !canvasContainerRef.current || isRendering) return;

    const renderPage = async () => {
      try {
        setIsRendering(true);
        const page = await pdf.getPage(currentPage);
        const viewport = page.getViewport({ scale });

        // Clear previous canvas
        const container = canvasContainerRef.current;
        if (!container) return;
        container.innerHTML = '';

        // Create canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return;

        canvas.height = viewport.height;
        canvas.width = viewport.width;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto';

        // Disable context menu on canvas
        canvas.oncontextmenu = (e) => {
          e.preventDefault();
          return false;
        };

        container.appendChild(canvas);

        // Render PDF page
        await page.render({
          canvasContext: context,
          viewport: viewport,
          canvas: canvas,
        }).promise;
        
        setIsRendering(false);
      } catch (err) {
        console.error('Error rendering page:', err);
        setIsRendering(false);
      }
    };

    renderPage();
  }, [pdf, currentPage, scale, isRendering]);

  // Block all protection-breaking actions
  useEffect(() => {
    const preventActions = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const preventKeyboardShortcuts = (e: KeyboardEvent) => {
      // Prevent Ctrl+P / Cmd+P (Print)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+S / Cmd+S (Save)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+C / Cmd+C (Copy)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', preventActions);
    document.addEventListener('keydown', preventKeyboardShortcuts);

    return () => {
      document.removeEventListener('contextmenu', preventActions);
      document.removeEventListener('keydown', preventKeyboardShortcuts);
    };
  }, []);

  const handlePrevPage = () => {
    if (currentPage > 1 && !isRendering) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < numPages && !isRendering) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleZoomIn = () => {
    if (!isRendering) {
      setScale((prev) => Math.min(prev + 0.2, 3.0));
    }
  };

  const handleZoomOut = () => {
    if (!isRendering) {
      setScale((prev) => Math.max(prev - 0.2, 0.5));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center text-red-600">
          <p className="mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-100" style={protectionStyles}>
      {/* Controls */}
      <div className="flex items-center justify-between bg-gray-800 text-white px-4 py-3 print:hidden">
        <div className="flex items-center space-x-4">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1 || isRendering}
            className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600 disabled:bg-gray-900 disabled:text-gray-600"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {currentPage} of {numPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === numPages || isRendering}
            className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600 disabled:bg-gray-900 disabled:text-gray-600"
          >
            Next
          </button>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={handleZoomOut}
            disabled={isRendering}
            className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600 disabled:bg-gray-900 disabled:text-gray-600"
            title="Zoom Out"
          >
            −
          </button>
          <span className="text-sm w-16 text-center">{Math.round(scale * 100)}%</span>
          <button
            onClick={handleZoomIn}
            disabled={isRendering}
            className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600 disabled:bg-gray-900 disabled:text-gray-600"
            title="Zoom In"
          >
            +
          </button>
        </div>
      </div>

      {/* PDF Canvas Container */}
      <div
        className="flex-1 overflow-auto bg-gray-200"
        style={{
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          return false;
        }}
      >
        <div ref={canvasContainerRef} className="py-4" />
      </div>
    </div>
  );
};
